import React, { useEffect, useRef, useState } from 'react';

/**
 * Flicker-counter — animates a numeric value up from 0 to the target with a
 * subtle "split-flap / airport board" digit flicker on each digit.
 *
 * Each visible digit briefly cycles through random digits before settling on
 * its final value. Works for percentages, currency, etc. via `prefix` / `suffix`.
 *
 * Triggers once on viewport entry.
 *
 * Props:
 *   value      — final numeric value, e.g. 1337.65 or -88.89
 *   decimals   — decimal places (default 0)
 *   duration   — total reveal duration in ms (default 1800)
 *   prefix     — text prefixed to the number (e.g. "+", "$")
 *   suffix     — text appended (e.g. "%")
 *   className  — extra classes for the wrapper
 */
export default function FlickerNumber({
  value,
  decimals = 0,
  duration = 1800,
  prefix = '',
  suffix = '',
  className = '',
  ...rest
}) {
  const ref = useRef(null);
  const [display, setDisplay] = useState(0);
  const [flicker, setFlicker] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        io.disconnect();

        // Animate value
        const start = performance.now();
        const easeOut = (t) => 1 - Math.pow(1 - t, 3);
        const tick = (now) => {
          const t = Math.min(1, (now - start) / duration);
          const eased = easeOut(t);
          setDisplay(value * eased);
          if (t < 1) requestAnimationFrame(tick);
          else setFlicker(false);
        };
        setFlicker(true);
        requestAnimationFrame(tick);
      },
      { threshold: 0.35 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [value, duration]);

  const formatted = display.toFixed(decimals);

  return (
    <span
      ref={ref}
      className={`flicker-number ${className}`}
      data-testid="flicker-number"
      style={{ display: 'inline-flex', alignItems: 'baseline' }}
      {...rest}
    >
      {prefix && <span>{prefix}</span>}
      {Array.from(formatted).map((c, i) => {
        if (/[0-9]/.test(c)) {
          return (
            <FlickerDigit
              key={i}
              char={c}
              active={flicker}
              i={i}
            />
          );
        }
        return <span key={i}>{c}</span>;
      })}
      {suffix && <span>{suffix}</span>}
    </span>
  );
}

function FlickerDigit({ char, active, i }) {
  const [d, setD] = useState(char);

  useEffect(() => {
    if (!active) {
      setD(char);
      return;
    }
    let alive = true;
    const interval = setInterval(() => {
      if (!alive) return;
      setD(String(Math.floor(Math.random() * 10)));
    }, 55 + i * 8);
    return () => {
      alive = false;
      clearInterval(interval);
      setD(char);
    };
  }, [active, char, i]);

  return (
    <span
      style={{
        display: 'inline-block',
        minWidth: '0.6em',
        textAlign: 'center',
        transition: 'transform 90ms ease',
      }}
    >
      {active ? d : char}
    </span>
  );
}
